import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';

import { NavigationBreadcrumbMenuComponent } from './navigation-breadcrumb-menu.component';
import {
  NavigationBreadcrumbExpansion,
  NavigationBreadcrumbLevel,
  NavigationBreadcrumbSelection,
} from './navigation-breadcrumb.model';

export * from './navigation-breadcrumb.model';

/**
 * Generic hierarchical breadcrumb with a searchable dropdown per level. The number of
 * levels is driven by the `levels` array. Purely presentational: the parent supplies each
 * level's current entity and options (with display-ready names — e.g. masking of encrypted
 * case names is the parent's concern), and reacts to `optionSelected` (e.g. by navigating).
 *
 * Dropdowns cascade: an option marked `expandable` renders an arrow and opens a submenu of
 * its `children` instead of committing, so the user can drill from a workspace down to the
 * leaf level before anything is loaded. Only leaf options commit, and `optionSelected`
 * carries the whole path — letting a host apply every level of the change at once rather
 * than one level at a time.
 *
 * Options load lazily: `levelOpened` fires for a level's own dropdown and `optionExpanded`
 * for a submenu, both before the children are needed. A level or option whose
 * options/children are still `null` shows a loading row.
 */
@Component({
  selector: 'ts-navigation-breadcrumb',
  templateUrl: './navigation-breadcrumb.component.html',
  styleUrls: ['./navigation-breadcrumb.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, MatMenuModule, MatTooltipModule, NavigationBreadcrumbMenuComponent],
})
export class NavigationBreadcrumbComponent {
  @Input()
  public set levels(value: NavigationBreadcrumbLevel[]) {
    this._levels = value ?? [];
    // Recomputed only when levels change, so the panels' input identity stays stable.
    this.activeIds = this._levels
      .map((level) => level.current?.id)
      .filter((id): id is string => !!id);
  }
  public get levels(): NavigationBreadcrumbLevel[] {
    return this._levels;
  }
  private _levels: NavigationBreadcrumbLevel[] = [];

  /** Extra class applied to the dropdown panels alongside `breadcrumb-menu`. The panels
   *  portal to the global overlay (outside the host's DOM), so this is the hook for hosts
   *  to re-theme them by overriding the `--ts-breadcrumb-menu-*` CSS custom properties. */
  @Input() menuClass?: string;

  @Output() optionSelected = new EventEmitter<NavigationBreadcrumbSelection>();
  /** Emits the level's key when its dropdown opens, so the parent can lazy-load options. */
  @Output() levelOpened = new EventEmitter<string>();
  /** Emits when a submenu opens, so the parent can lazy-load that option's children. */
  @Output() optionExpanded = new EventEmitter<NavigationBreadcrumbExpansion>();

  /** Ids of the currently-open entities; a row matching one of them is ticked. */
  public activeIds: string[] = [];

  /** Class list for every panel, nested ones included — they each portal separately. */
  public get panelClass(): string {
    return this.menuClass ? `breadcrumb-menu ${this.menuClass}` : 'breadcrumb-menu';
  }

  /**
   * Track levels by key so re-emissions of `levels` update the existing embedded views in
   * place instead of recreating them. Without this, an OPEN dropdown belongs to a destroyed
   * view and freezes with stale options (e.g. options lazy-loaded via `levelOpened` would
   * never appear).
   */
  public trackByLevelKey(_index: number, level: NavigationBreadcrumbLevel): string {
    return level.key;
  }
}
